import { useState } from "react";
import { Link } from "react-router-dom";
import { signOut } from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { POLICY_VERSIONS } from "../constants/policies";
import PolicyLanguageToggle, {
  usePolicyLanguage,
} from "../components/PolicyLanguageToggle";

const copy = {
  es: {
    title: "Antes de comenzar",
    introduction:
      "BioIndex necesita que aceptes sus políticas para proteger tu cuenta, tus fotografías y nuestra comunidad. Aquí tienes un resumen breve; los documentos completos están disponibles en cada sección.",
    policies: [
      {
        title: "Condiciones de uso",
        summary: "Las reglas para utilizar BioIndex y publicar contenido.",
        linkLabel: "Leer condiciones completas",
        to: "/terms",
      },
      {
        title: "Política de privacidad",
        summary:
          "Qué información recopilamos, cómo la utilizamos y qué opciones tienes.",
        linkLabel: "Leer política completa",
        to: "/privacy",
      },
      {
        title: "Normas de la comunidad",
        summary:
          "Las reglas para mantener BioIndex seguro, respetuoso y útil.",
        linkLabel: "Leer normas completas",
        to: "/community-guidelines",
      },
    ],
    agreement:
      "Confirmo que tengo al menos 18 años, acepto las Condiciones de uso y las Normas de la comunidad, y reconozco la Política de privacidad.",
    saving: "Guardando aceptación...",
    continue: "Aceptar y entrar a BioIndex",
    signOut: "Cerrar sesión",
    error:
      "No pudimos guardar tu aceptación. Revisa tu conexión e inténtalo nuevamente.",
  },
  en: {
    title: "Before you continue",
    introduction:
      "BioIndex needs you to accept its policies to protect your account, your photographs, and our community. Here is a brief summary; the complete documents are available in each section.",
    policies: [
      {
        title: "Terms of Use",
        summary: "The rules for using BioIndex and publishing content.",
        linkLabel: "Read the complete Terms",
        to: "/terms",
      },
      {
        title: "Privacy Policy",
        summary:
          "What information we collect, how we use it, and the choices available to you.",
        linkLabel: "Read the complete Policy",
        to: "/privacy",
      },
      {
        title: "Community Guidelines",
        summary:
          "The rules that help keep BioIndex safe, respectful, and useful.",
        linkLabel: "Read the complete Guidelines",
        to: "/community-guidelines",
      },
    ],
    agreement:
      "I confirm that I am at least 18 years old, agree to the Terms of Use and Community Guidelines, and acknowledge the Privacy Policy.",
    saving: "Saving agreement...",
    continue: "Agree and enter BioIndex",
    signOut: "Sign out",
    error:
      "Your agreement could not be saved. Please check your connection and try again.",
  },
};

export default function PolicyConsentPage({ user }) {
  const [language, setLanguage] = usePolicyLanguage();
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const content = copy[language];

  async function acceptPolicies() {
    if (!agreed || !user || saving) return;

    setSaving(true);
    setError("");

    try {
      await setDoc(
        doc(db, "profile", user.uid),
        {
          policyAcceptance: {
            termsVersion: POLICY_VERSIONS.terms,
            privacyVersion: POLICY_VERSIONS.privacy,
            communityGuidelinesVersion: POLICY_VERSIONS.communityGuidelines,
            language,
            acceptedAt: serverTimestamp(),
          },
        },
        { merge: true },
      );
    } catch (acceptanceError) {
      console.error("Failed to save policy acceptance:", acceptanceError);
      setError(content.error);
      setSaving(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#374049] px-4 py-6 text-[#374049] sm:py-8">
      <section className="mx-auto max-w-2xl overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FCFFF6] shadow-xl">
        <div className="bg-[#374049] px-6 py-5">
          <img
            src="/BioIndex Prototype Banner.png"
            alt="BioIndex"
            className="mx-auto max-h-28 w-full max-w-md object-contain sm:max-h-32"
          />
        </div>

        <div className="px-5 py-6 sm:px-10 sm:py-8">
          <div className="flex justify-center sm:justify-end">
            <PolicyLanguageToggle
              language={language}
              onChange={setLanguage}
            />
          </div>

          <h1
            className="mt-6 text-center text-3xl font-semibold uppercase tracking-[0.1em] sm:text-4xl"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            {content.title}
          </h1>

          <p className="mx-auto mt-3 max-w-lg text-center text-sm leading-6 text-[#374049]/75">
            {content.introduction}
          </p>

          <div className="mt-6 space-y-3">
            {content.policies.map((policy, index) => (
              <article
                key={policy.to}
                className="flex items-start gap-3 rounded-2xl border border-[#374049]/10 bg-white px-4 py-3.5 shadow-sm sm:gap-4 sm:px-5"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#24C3BE]/10 text-sm font-bold text-[#01767A]">
                  {index + 1}
                </span>

                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold text-[#374049]">
                    {policy.title}
                  </h2>

                  <p className="mt-1 text-xs leading-5 text-[#374049]/65 sm:text-sm">
                    {policy.summary}
                  </p>

                  <Link
                    to={policy.to}
                    className="mt-2 inline-flex text-xs font-semibold text-[#01767A] underline decoration-[#24C3BE]/50 underline-offset-2 transition hover:text-[#24C3BE]"
                  >
                    {policy.linkLabel} →
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border border-[#93CA87]/45 bg-[#93CA87]/10 p-4 transition hover:border-[#24C3BE]/60">
            <input
              type="checkbox"
              checked={agreed}
              disabled={saving}
              onChange={(event) => setAgreed(event.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 accent-[#24C3BE]"
            />

            <span className="text-sm leading-6 text-[#374049]/80">
              {content.agreement}
            </span>
          </label>

          {error && (
            <p
              className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              role="alert"
            >
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={acceptPolicies}
            disabled={!agreed || saving}
            className="mt-5 w-full rounded-xl bg-[#24C3BE] px-5 py-3 text-sm font-semibold text-[#374049] transition hover:bg-[#20b2ae] focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/40 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? content.saving : content.continue}
          </button>

          <button
            type="button"
            onClick={() => signOut(auth)}
            disabled={saving}
            className="mt-3 w-full rounded-xl border border-[#374049]/30 px-5 py-2.5 text-sm font-medium transition hover:bg-[#374049]/5 disabled:opacity-50"
          >
            {content.signOut}
          </button>
        </div>
      </section>
    </main>
  );
}
